import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { Modal } from '../common/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircle, XCircle, UserMinus } from 'lucide-react';

interface Horse {
  id: string;
  name: string;
  breed: string;
  gender: 'mare' | 'stallion' | 'gelding';
  owner: {
    id: string;
    email: string;
  };
  dateOfBirth?: string;
  notes?: string;
  coRiders?: {
    [key: string]: {
      id: string;
      email: string;
      name?: string;
      status: 'pending' | 'approved' | 'denied';
    };
  };
}

interface HorseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (horse: Omit<Horse, 'id'>) => void;
  horse?: Horse | null;
}

interface FormData {
  name: string;
  breed: string;
  gender: 'mare' | 'stallion' | 'gelding';
  dateOfBirth: string;
  notes: string;
  owner: {
    id: string;
    email: string;
  };
  coRiders: {
    [key: string]: {
      id: string;
      email: string;
      name?: string;
      status: 'pending' | 'approved' | 'denied';
    };
  };
}

// Liste der Pferderassen
const horseBreeds = [
  { value: 'Arabisches Vollblut', label: 'Arabisches Vollblut' },
  { value: 'Thoroughbred', label: 'Thoroughbred' },
  { value: 'Quarter Horse', label: 'Quarter Horse' },
  { value: 'Friese', label: 'Friese' },
  { value: 'Isländer', label: 'Isländer' },
  { value: 'Hannoveraner', label: 'Hannoveraner' },
  { value: 'Trakehner', label: 'Trakehner' },
  { value: 'Andalusier', label: 'Andalusier' },
  { value: 'Lipizzaner', label: 'Lipizzaner' },
  { value: 'Oldenburger', label: 'Oldenburger' },
  { value: 'Westfale', label: 'Westfale' },
  { value: 'Andere', label: 'Andere' }, // Option für individuelle Eingabe
];

export const HorseModal: React.FC<HorseModalProps> = ({
  isOpen,
  onClose,
  onSave,
  horse,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    breed: '',
    gender: 'gelding',
    dateOfBirth: '',
    notes: '',
    owner: { id: '', email: '' },
    coRiders: {},
  });
  const [isCustomBreed, setIsCustomBreed] = useState(false);
  const [error, setError] = useState<string | null>(null); // Fehlerzustand hinzugefügt

  useEffect(() => {
    if (horse) {
      setFormData({
        name: horse.name || '',
        breed: horse.breed || '',
        gender: horse.gender || 'gelding',
        dateOfBirth: horse.dateOfBirth || '',
        notes: horse.notes || '',
        owner: horse.owner || { id: '', email: '' },
        coRiders: horse.coRiders || {},
      });
      setIsCustomBreed(!horseBreeds.some((breed) => breed.value === horse.breed));
    } else {
      setFormData({
        name: '',
        breed: '',
        gender: 'gelding',
        dateOfBirth: '',
        notes: '',
        owner: { id: '', email: '' },
        coRiders: {},
      });
      setIsCustomBreed(false);
    }
    setError(null); // Fehler zurücksetzen beim Laden eines neuen Pferdes
  }, [horse]);

  const handleCoRiderAction = async (
    coRiderId: string,
    action: 'approve' | 'deny' | 'remove'
  ) => {
    try {
      setFormData((prev) => ({
        ...prev,
        coRiders: {
          ...prev.coRiders,
          [coRiderId]: {
            ...prev.coRiders[coRiderId],
            status: action === 'approve' ? 'approved' : 'denied',
          },
        },
      }));

      if (action === 'deny') {
        setTimeout(() => {
          setFormData((prev) => {
            const { [coRiderId]: removed, ...remaining } = prev.coRiders;
            return { ...prev, coRiders: remaining };
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Error managing co-rider:', error);
      setError('Beim Verwalten der Reitbeteiligung ist ein Fehler aufgetreten.');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Hier kannst du zusätzliche Validierungen hinzufügen
      onSave(formData);
      onClose();
    } catch (err) {
      console.error('Error saving horse:', err);
      setError('Beim Speichern des Pferdes ist ein Fehler aufgetreten.');
    }
  };

  const activeCoRiders = Object.entries(formData.coRiders || {}).filter(
    ([_, coRider]) => coRider.status !== 'denied'
  );
  const canManageCoRiders =
    user && (horse?.owner.id === user.id || user?.uid === horse?.owner.id);

  const renderFooter = () => (
    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
      <button
        type="button"
        onClick={onClose}
        className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        Abbrechen
      </button>
      <button
        type="submit"
        form="horse-form"
        className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors"
      >
        {horse ? 'Änderungen speichern' : 'Pferd hinzufügen'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={horse ? 'Pferd bearbeiten' : 'Neues Pferd'}
      footer={renderFooter()}
      size="lg"
    >
      <form id="horse-form" onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Rasse</label>
            {!isCustomBreed ? (
              <Select
                options={horseBreeds}
                value={horseBreeds.find((breed) => breed.value === formData.breed) || null}
                onChange={(selectedOption) => {
                  if (selectedOption?.value === 'Andere') {
                    setIsCustomBreed(true);
                    setFormData((prev) => ({ ...prev, breed: '' }));
                  } else if (selectedOption) {
                    setFormData((prev) => ({ ...prev, breed: selectedOption.value }));
                  }
                }}
                placeholder="Wählen Sie eine Rasse..."
                isSearchable
              />
            ) : (
              <div>
                <input
                  type="text"
                  value={formData.breed}
                  onChange={(e) => setFormData((prev) => ({ ...prev, breed: e.target.value }))}
                  placeholder="Individuelle Rasse eingeben"
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  required
                />
                <button
                  type="button"
                  onClick={() => setIsCustomBreed(false)}
                  className="mt-2 text-sm text-indigo-600 hover:underline"
                >
                  Zur Vorauswahl zurückkehren
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Geschlecht</label>
            <select
              value={formData.gender}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  gender: e.target.value as 'mare' | 'stallion' | 'gelding',
                }))
              }
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              required
            >
              <option value="mare">Stute</option>
              <option value="stallion">Hengst</option>
              <option value="gelding">Wallach</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Geburtsdatum</label>
            <input
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => setFormData((prev) => ({ ...prev, dateOfBirth: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notizen</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        {horse && (canManageCoRiders || activeCoRiders.length > 0) && (
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Reitbeteiligungen
            </h4>
            <div className="space-y-3">
              {activeCoRiders.map(([id, coRider]) => (
                <div key={id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {coRider.email}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          coRider.status === 'approved'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}
                      >
                        {coRider.status === 'approved' ? 'Bestätigt' : 'Ausstehend'}
                      </span>
                    </div>
                  </div>

                  {canManageCoRiders && (
                    <div className="flex items-center space-x-2 ml-4">
                      {coRider.status === 'pending' && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleCoRiderAction(id, 'approve')}
                            className="p-2 rounded-full text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                            title="Anfrage annehmen"
                          >
                            <CheckCircle className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCoRiderAction(id, 'deny')}
                            className="p-2 rounded-full text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            title="Anfrage ablehnen"
                          >
                            <XCircle className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {coRider.status === 'approved' && (
                        <button
                          type="button"
                          onClick={() => handleCoRiderAction(id, 'remove')}
                          className="p-2 rounded-full text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          title="Reitbeteiligung entfernen"
                        >
                          <UserMinus className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fehleranzeige */}
        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
            <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
          </div>
        )}
      </form>
    </Modal>
  );
};
